<script setup lang="ts">
import type { Maker } from "~/types";

const makersStore = useMakersStore();
const { attachmentUrl, staticUrl } = useImageUrl();
const { runDeepSearch, isSearching } = useDeepSearch();
const route = useRoute();
const router = useRouter();

// Cap the dropdown so it stays a quick preview, not a full results list —
// once there are more than this, a "view all results" action takes over.
const MAX_DROPDOWN_RESULTS = 3;

const emit = defineEmits<{
  returnSearch: [query: string];
  searchSubmit: [query: string];
}>();

const searchTerm = ref("");
const isFocused = ref(false);
const localResults = ref<Maker[]>([]);
// True once a search for the current term has completed, so the dropdown stays
// open to show "no results found" instead of flashing the spinner then closing.
const hasSearched = ref(false);
let searchTimeout: ReturnType<typeof setTimeout> | null = null;

const showResults = computed(
  () =>
    isFocused.value &&
    searchTerm.value.length > 2 &&
    (localResults.value.length > 0 || isSearching.value || hasSearched.value),
);

const searchResults = computed(() => localResults.value);
// Only the first few makers render in the dropdown preview; the rest live
// behind "view all results", which routes to the full /search page.
const displayedResults = computed(() => localResults.value.slice(0, MAX_DROPDOWN_RESULTS));
const hasMoreResults = computed(() => localResults.value.length > MAX_DROPDOWN_RESULTS);

function handleInput() {
  emit("returnSearch", searchTerm.value);

  if (searchTimeout) clearTimeout(searchTimeout);

  if (searchTerm.value.length <= 2) {
    localResults.value = [];
    hasSearched.value = false;
    return;
  }

  searchTimeout = setTimeout(async () => {
    // useDeepSearch aborts the previous request and returns null if a newer
    // keystroke has superseded this one \u2014 ignore a null so a stale response
    // never overwrites fresher results. Also re-check the term against the live
    // input: the <=2 backspace branch above and clearSearch reset local state
    // without going through runDeepSearch, so an in-flight request for the old
    // term would otherwise still resolve as "latest" and repopulate stale results.
    const term = searchTerm.value;
    const results = await runDeepSearch(term);
    if (results !== null && term === searchTerm.value) {
      localResults.value = results;
      hasSearched.value = true;
    }
  }, 200);
}

function clearSearch() {
  searchTerm.value = "";
  localResults.value = [];
  hasSearched.value = false;
  emit("returnSearch", "");
  // Drop `?q=` from the URL so clearing the input also clears the location bar
  // (on /search the query watcher then resets back to the carousel landing).
  // Other filter params (date/tags/cuisines) are preserved.
  if (route.query.q) {
    const query = { ...route.query };
    delete query.q;
    router.replace({ query });
  }
}

function handleEnter() {
  // Pre-populate store results before navigating so the search page renders
  // them immediately instead of waiting for a second API round-trip.
  if (localResults.value.length > 0) {
    makersStore.filteredMakerCategories = localResults.value;
  }
  emit("searchSubmit", searchTerm.value);
  isFocused.value = false;
}

function handleBlur() {
  setTimeout(() => {
    isFocused.value = false;
  }, 200);
}

function onImageError(e: Event) {
  const target = e.target as HTMLImageElement;
  target.src = staticUrl("/Images/site-images/placeholder.png");
}

function formatPrice(priceGross: number | string): string {
  return `£${(parseFloat(String(priceGross)) / 100).toFixed(2)}`;
}
</script>

<template>
  <div class="search-bar">
    <SearchInput
      v-model="searchTerm"
      placeholder="What are you looking for?"
      name="search"
      @input="handleInput"
      @clear="clearSearch"
      @keydown.enter="handleEnter"
      @focus="isFocused = true"
      @blur="handleBlur"
    />
    <Transition name="search-results">
      <div
        v-show="showResults"
        class="search-bar__results"
      >
      <p class="light-grey-text small-text search-bar__results--count">
        <fa
          :icon="['fal', isSearching ? 'spinner-third' : 'magnifying-glass']"
          :spin="isSearching"
        />
        {{
          isSearching
            ? "Searching…"
            : searchResults.length
              ? `${searchResults.length} Maker${searchResults.length > 1 ? "s" : ""} found`
              : "No results found"
        }}
      </p>
      <div class="search-bar__results--items">
        <div
          v-for="result in displayedResults"
          :key="result.id"
        >
          <NuxtLink
            class="item"
            :to="`/store/${result.slug}/`"
          >
            <div class="item__image">
              <img
                v-if="result.attachments?.profile"
                loading="lazy"
                decoding="async"
                width="50"
                height="50"
                :src="attachmentUrl(result.attachments.profile, 'small')"
                :alt="`${result.name} profile`"
                @error="onImageError"
              />
              <img
                v-else
                loading="lazy"
                decoding="async"
                width="50"
                height="50"
                :src="staticUrl('/Images/site-images/placeholder.png')"
                :alt="`${result.name} placeholder`"
              />
            </div>
            <div class="item__text">
              <p class="grey-text item__text--title">{{ result.name }}</p>
              <p
                v-if="result.description"
                class="light-grey-text small-text item__text--subtitle"
                v-html="`${result.description.slice(0, 50)}...`"
              ></p>
            </div>
          </NuxtLink>
          <NuxtLink
            v-for="product in (result.products || []).filter((p: Record<string, unknown>) => p.is_active !== false)"
            :key="(product as Record<string, unknown>).id as string"
            class="item"
            :to="`/store/${result.slug}/?product=${(product as Record<string, unknown>).id}`"
          >
            <div class="item__text">
              <p class="grey-text small-text item__text--title">
                {{ (product as Record<string, unknown>).name }}
              </p>
              <p
                v-if="(product as Record<string, unknown>).price_gross"
                class="light-grey-text item__text--subtitle small-text heavy"
              >
                {{ formatPrice((product as Record<string, unknown>).price_gross as number) }}
              </p>
            </div>
          </NuxtLink>
        </div>
      </div>
      <!-- Always offered when there are results — acts exactly like pressing
           Enter (handleEnter), routing to the full /search results page. Label
           reflects whether the preview is showing everything ("View results")
           or only the first few ("View all results"). mousedown.prevent keeps
           the input focused so the click lands before the blur closes the
           dropdown. -->
      <button
        v-if="searchResults.length"
        type="button"
        class="search-bar__results--view-all"
        @mousedown.prevent="handleEnter"
      >
        {{ hasMoreResults ? "View all results" : "View results" }}
      </button>
      </div>
    </Transition>
  </div>
</template>

<style lang="scss">
.search-bar {
  // Position context for the absolutely-positioned results dropdown so the
  // dropdown can match the input width via width: 100% instead of guessing
  // a viewport percentage.
  position: relative;
  // The outer wrapper owns the border, radius, background, and focus glow.
  // Dimensions match the global text-input rule in main.scss (var(--tap)
  // height, --radius-sm corner, same border + focus ring) so the search
  // bar reads as part of the same input family as every other field on
  // the site, not a one-off pill.
  // The input row itself (icon/field/clear button) lives in SearchInput.vue's
  // global .search-bar__input styles now — this component only owns the
  // results dropdown below.
  // Results dropdown sits flush under the input — width: 100% of the parent
  // .search-bar so it always matches the input pill, not a viewport guess.
  &__results {
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
    position: absolute;
    // Anchor the dropdown at its top edge so it expands downward from under
    // the search bar, never up over the input where the cursor sits.
    transform-origin: top center;
    top: calc(var(--tap) + 0.25rem);
    left: 0;
    max-height: 20rem;
    border-radius: var(--radius-sm);
    z-index: 7777;
    width: 100%;
    background: var(--color-white-1);
    border: 0.0625rem solid var(--color-grey-3);
    padding: 0 0 1rem;
		&::-webkit-scrollbar {
			display: none;
		}

    @media (max-width: $tablet) {
      left: 0;
      // Sit just below the input — `top: 5rem` was leaving a giant
      // empty band that showed the page underneath. tap + 0.25rem
      // mirrors the desktop offset.
      top: calc(var(--tap) + 0.25rem);
      width: 100%;
      border: 0.0625rem solid var(--color-grey-3);
      border-radius: var(--radius-sm);
      // Shorter cap: results should feel like a focused overlay, not
      // a full-page takeover. ~22rem (≈10 rows on a 16px base) is
      // plenty for browsing and leaves clear sight of the cart pill +
      // page below.
      max-height: 22rem;
      height: auto;
      overflow-x: hidden;
    }
    &--count {
      // Sticky header pinned to the top of the scroll container so the count
      // stays visible while results scroll underneath it (not `fixed`, which
      // anchored it to the viewport and let result text clash through it).
      margin: 0;
      padding: 1rem;
      position: sticky;
      top: 0;
      z-index: 1;
      background: var(--color-white-1);
    }
    &--items {
      .item {
        padding: 0.5rem 1rem;
        list-style: none;
        display: flex;
        align-items: center;
        cursor: pointer;
        text-decoration: none !important;
        box-sizing: border-box;
        // Hover: muted pink wash, title underline + primary text. No purple.
        &:hover {
          background-color: var(--color-pink-4);
          .item__text--title {
            text-decoration: underline;
            color: var(--color-pink-1) !important;
          }
        }
        &__text {
          // Flex-grow + min-width:0 so the title/subtitle wrap and
          // ellipsis inside the row instead of pushing the row past
          // the dropdown's right edge.
          flex: 1 1 auto;
          min-width: 0;
          display: flex;
          flex-direction: column;
          &--title,
          &--subtitle {
            margin-left: 10px;
            margin-top: 0;
            width: 100%;
            font-weight: var(--weight-light) !important;
            overflow: hidden;
            display: -webkit-box;
            p {
              margin: 0;
            }
          }
          &--title {
            -webkit-line-clamp: 1;
            overflow: hidden;
            display: -webkit-box;
            max-height: 1.2rem;
            word-wrap: break-word;
            font-family: inherit;
          }
          &--subtitle {
            overflow: hidden;
            display: -webkit-box;
            -webkit-line-clamp: 2;
            max-height: 2.5rem;
            word-wrap: break-word;
            font-family: inherit;
          }
        }
        &__image {
          flex: 0 0 3rem;
          width: 3rem;
          overflow: hidden;
          img {
            width: 100%;
            aspect-ratio: 1;
            object-fit: cover;
            border-radius: 0.4rem;
          }
        }
      }
    }
    // Footer action: routes to the full /search page (same as pressing Enter).
    // Full-width, divider on top, primary-pink label so it reads as the clear
    // next step out of the preview.
    &--view-all {
      display: block;
      width: 100%;
      padding: 0.75rem 1rem;
      background: transparent;
      border: 0;
      border-top: 0.0625rem solid var(--color-grey-3);
      color: var(--color-pink-1);
      font-family: inherit;
      font-size: var(--text-sm);
      font-weight: var(--weight-semibold);
      text-align: center;
      cursor: pointer;
      transition: background-color var(--dur-fast) var(--ease-out);
      &:hover {
        background-color: var(--color-pink-4);
        text-decoration: underline;
      }
    }
  }
}

// Dropdown reveal: fade + expand downward from the search bar's bottom edge
// (transform-origin: top on .search-bar__results). Replaces animate.css
// fadeInDown, which rendered the panel above the input and slid it down over
// the cursor — causing stray clicks on results.
.search-results-enter-active,
.search-results-leave-active {
  transition:
    opacity var(--dur-fast) var(--ease-out),
    transform var(--dur-fast) var(--ease-out);
}
.search-results-enter-from,
.search-results-leave-to {
  opacity: 0;
  transform: scaleY(0.92) translateY(-0.25rem);
}
</style>
